import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Chip,
  DataTable,
  Field,
  Input,
  Modal,
  StateBlock,
  useToast,
  type Column,
} from '@w7/ui'
import { useMemo, useState, type FormEvent } from 'react'
import {
  cmApi,
  type SecurityDuplicateIpDetailRow,
  type SecurityDuplicateIpRow,
} from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { canBlockIp } from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import { CmPanel } from '@/features/catalog/CmPanel'

function toDateInputValue(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function dayStartIso(ymd: string): string {
  return new Date(`${ymd}T00:00:00`).toISOString()
}

function dayEndIso(ymd: string): string {
  return new Date(`${ymd}T23:59:59.999`).toISOString()
}

/**
 * Security → Multiple IPs — Lab `/duplicate-ip` (IPs shared by ≥2 players).
 * Filters + table + Block (blocked-ip save) + IP bet detail.
 */
export function ManageIpPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const me = useCmMe()
  const canBlock = canBlockIp(me.data?.caller)
  const today = toDateInputValue(new Date())

  const [page, setPage] = useState(1)
  const [keywordDraft, setKeywordDraft] = useState('')
  const [playerDraft, setPlayerDraft] = useState('')
  const [fromDraft, setFromDraft] = useState(today)
  const [toDraft, setToDraft] = useState(today)
  const [applied, setApplied] = useState({
    keyword: '',
    player: '',
    fromDate: dayStartIso(today),
    toDate: dayEndIso(today),
  })
  const [pendingBlock, setPendingBlock] = useState<SecurityDuplicateIpRow | null>(null)
  const [detailIp, setDetailIp] = useState<string | null>(null)
  const pageSize = 50

  const listQ = useQuery({
    queryKey: ['cm', 'security', 'manage-ip', page, pageSize, applied],
    queryFn: () =>
      cmApi.securityDuplicateIpList(authHeaders(), {
        page,
        pageSize,
        keyword: applied.keyword || undefined,
        player: applied.player || undefined,
        fromDate: applied.fromDate,
        toDate: applied.toDate,
      }),
  })

  const detailQ = useQuery({
    queryKey: ['cm', 'security', 'manage-ip-detail', detailIp, applied.fromDate, applied.toDate],
    queryFn: () =>
      cmApi.securityDuplicateIpDetail(authHeaders(), {
        ip: detailIp!,
        page: 1,
        pageSize: 50,
        fromDate: applied.fromDate,
        toDate: applied.toDate,
      }),
    enabled: Boolean(detailIp),
  })

  const blockM = useMutation({
    mutationFn: (row: SecurityDuplicateIpRow) =>
      cmApi.securityBlockedIpSave(authHeaders(), {
        ip: row.ip,
        comment: 'Blocked From Duplicate IP',
      }),
    onSuccess: async (_res, row) => {
      toast.success(`${row.ip} IP Blocked successfully.`)
      setPendingBlock(null)
      await qc.invalidateQueries({ queryKey: ['cm', 'security', 'blocked-ip'] })
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : String(err)),
  })

  const rows = listQ.data?.rows ?? []
  const total = listQ.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const detailRows = detailQ.data?.rows ?? []

  const columns = useMemo((): Column<SecurityDuplicateIpRow>[] => {
    const cols: Column<SecurityDuplicateIpRow>[] = [
      {
        id: 'n',
        header: '#',
        width: 48,
        numeric: true,
        cell: (_row, i) => (page - 1) * pageSize + i + 1,
      },
      {
        id: 'ip',
        header: 'IP',
        cell: (r) => (
          <button
            type="button"
            className="w7-linkish"
            onClick={() => setDetailIp(r.ip)}
            title="IP detail"
          >
            {r.ip}
          </button>
        ),
      },
      {
        id: 'count',
        header: 'Duplicate Count',
        numeric: true,
        cell: (r) => r.playerCount,
      },
      {
        id: 'players',
        header: 'Players',
        cell: (r) =>
          r.players.length ? (
            <div className="cm-id-chip-row">
              {r.players.map((p) => (
                <Chip key={p.username} tone="info" size="sm">
                  {p.username}
                </Chip>
              ))}
            </div>
          ) : (
            '—'
          ),
      },
    ]
    if (canBlock) {
      cols.push({
        id: 'action',
        header: 'Action',
        cell: (r) => (
          <Button type="button" variant="danger" size="sm" onClick={() => setPendingBlock(r)}>
            Block
          </Button>
        ),
      })
    }
    return cols
  }, [canBlock, page, pageSize])

  const detailColumns = useMemo(
    (): Column<SecurityDuplicateIpDetailRow>[] => [
      { id: 'user', header: 'Username', cell: (r) => r.username || '—' },
      { id: 'event', header: 'Event', cell: (r) => r.eventName || '—' },
      { id: 'market', header: 'Market', cell: (r) => r.marketName || '—' },
      { id: 'sel', header: 'Selection', cell: (r) => r.selectionName || '—' },
      { id: 'side', header: 'Side', cell: (r) => r.side || '—' },
      { id: 'stake', header: 'Stake', numeric: true, cell: (r) => r.stake },
      { id: 'odds', header: 'Odds', numeric: true, cell: (r) => r.odds },
      { id: 'status', header: 'Status', cell: (r) => r.status || '—' },
      {
        id: 'when',
        header: 'Placed',
        cell: (r) => (r.placedAt ? new Date(r.placedAt).toLocaleString() : '—'),
      },
    ],
    [],
  )

  return (
    <CmPanel
      title="Multiple IPs"
      subtitle="IPs used by more than one player (Lab Duplicate Ips)"
    >
      <div className="cm-security">
        <form
          className="cm-toolbar cm-group-toolbar"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            if (!fromDraft || !toDraft) {
              toast.error('From Date and To Date are required')
              return
            }
            if (toDraft < fromDraft) {
              toast.error('To Date must be on or after From Date')
              return
            }
            setPage(1)
            setApplied({
              keyword: keywordDraft.trim(),
              player: playerDraft.trim(),
              fromDate: dayStartIso(fromDraft),
              toDate: dayEndIso(toDraft),
            })
          }}
        >
          <Field label="Enter IP">
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={keywordDraft}
                onChange={(e) => setKeywordDraft(e.target.value)}
                placeholder="Search Here..."
              />
            )}
          </Field>
          <Field label="Enter Player">
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={playerDraft}
                onChange={(e) => setPlayerDraft(e.target.value)}
                placeholder="Search Here..."
              />
            )}
          </Field>
          <Field label="From Date" required>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="date"
                required
                value={fromDraft}
                onChange={(e) => setFromDraft(e.target.value)}
              />
            )}
          </Field>
          <Field label="To Date" required>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="date"
                required
                value={toDraft}
                min={fromDraft || undefined}
                onChange={(e) => setToDraft(e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" variant="secondary" busy={listQ.isFetching}>
            Search
          </Button>
        </form>

        {listQ.isError ? (
          <div className="cm-hint" role="alert">
            <p>{(listQ.error as Error).message}</p>
            <Button type="button" variant="secondary" onClick={() => void listQ.refetch()}>
              Retry
            </Button>
          </div>
        ) : null}

        <DataTable
          label="Duplicate IPs"
          density="compact"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          loading={listQ.isFetching && !rows.length}
          empty={
            !listQ.isError ? (
              <div className="cm-security-empty">
                <StateBlock icon="search" title="No data found">
                  No duplicate IPs for this date range.
                </StateBlock>
              </div>
            ) : null
          }
        />

        {totalPages > 1 ? (
          <div className="cm-pager">
            <Button
              type="button"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span>
              Page {page} of {totalPages} ({total})
            </span>
            <Button
              type="button"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        ) : null}
      </div>

      <Modal
        open={Boolean(pendingBlock)}
        onClose={() => {
          if (blockM.isPending) return
          setPendingBlock(null)
        }}
        title="Block IP"
        tone="danger"
        closeDisabled={blockM.isPending}
        footer={
          <div className="cm-form-actions">
            <Button
              type="button"
              variant="secondary"
              disabled={blockM.isPending}
              onClick={() => setPendingBlock(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              busy={blockM.isPending}
              onClick={() => pendingBlock && blockM.mutate(pendingBlock)}
            >
              Block
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to Block IP {pendingBlock?.ip}?</p>
      </Modal>

      <Modal
        open={Boolean(detailIp)}
        onClose={() => setDetailIp(null)}
        title={detailIp ? `IP ${detailIp}` : 'IP detail'}
      >
        {detailQ.isError ? (
          <p role="alert">{(detailQ.error as Error).message}</p>
        ) : (
          <DataTable
            label={`Bets for ${detailIp ?? 'IP'}`}
            density="compact"
            columns={detailColumns}
            rows={detailRows}
            rowKey={(r) => r.id}
            loading={detailQ.isFetching && !detailRows.length}
            empty={
              <div className="cm-security-empty">
                <StateBlock icon="search" title="No Bets Found" />
              </div>
            }
          />
        )}
      </Modal>
    </CmPanel>
  )
}
